<template>
        <div class="min-h-screen bg-gray-50 font-sans">
                <!-- Header -->
                <header class="bg-white border-b border-gray-200 py-4 px-6">
                        <div class="flex justify-between items-center flex-wrap gap-4">
                                <div class="flex items-center gap-4">
                                        <button
                                                type="button"
                                                class="flex items-center justify-center w-10 h-10 bg-transparent border border-gray-200 rounded-lg cursor-pointer text-gray-500"
                                                @click="goBack"
                                        >
                                                <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
                                                </svg>
                                        </button>
                                        <div>
                                                <h1 class="text-2xl font-semibold text-gray-900 m-0">
                                                        {{ shop?.name || 'Loading...' }}
                                                </h1>
                                                <p class="text-sm text-gray-500 mt-1 mb-0">
                                                        Shop Integration #{{ routeId }}<span v-if="shop?.created"> &middot; Created {{ formatDate(shop.created) }}</span>
                                                </p>
                                        </div>
                                </div>
                                <div v-if="shop" class="flex items-center gap-3">
                                        <span v-if="hasChanges" class="text-xs text-orange-7 bg-orange-1 py-1 px-3 rounded-full font-medium">Unsaved changes</span>
                                        <button
                                                type="button"
                                                :disabled="!hasChanges || isSaving || !!stylingError"
                                                :title="stylingError ? 'Fix the JSON error in the Styling section before saving' : undefined"
                                                class="inline-flex items-center gap-2 py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                @click="save"
                                        >
                                                <svg v-if="isSaving" class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                                                </svg>
                                                {{ isSaving ? 'Saving...' : 'Save Changes' }}
                                        </button>
                                </div>
                        </div>
                </header>

                <!-- Loading State -->
                <div v-if="isLoading" class="flex justify-center items-center py-16 px-4">
                        <svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
                                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span class="ml-3 text-gray-500 text-sm">Loading shop...</span>
                </div>

                <!-- Error State -->
                <main v-else-if="loadError" class="p-6">
                        <div class="bg-white rounded-xl shadow-sm text-center py-12 px-6">
                                <svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
                                        <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                </svg>
                                <p class="text-gray-900 text-sm font-medium m-0">Could not load this shop integration</p>
                                <p class="text-gray-500 text-sm mt-1 mb-4">{{ loadError }}</p>
                                <div class="flex justify-center gap-3">
                                        <button
                                                type="button"
                                                class="py-2.5 px-4 border border-gray-300 bg-white text-gray-700 rounded-lg text-sm cursor-pointer"
                                                @click="goBack"
                                        >
                                                Back to Shops
                                        </button>
                                        <button
                                                type="button"
                                                class="py-2.5 px-4 bg-orange-7 text-white border-none rounded-lg text-sm cursor-pointer"
                                                @click="fetchShop"
                                        >
                                                Retry
                                        </button>
                                </div>
                        </div>
                </main>

                <!-- Main Content -->
                <main v-else-if="shop" class="p-6">
                        <div class="flex flex-col gap-4">
                                <!-- General -->
                                <section class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200">
                                                <h3 class="text-base font-semibold text-gray-900 m-0">General</h3>
                                                <p class="text-sm text-gray-500 mt-1 mb-0">Basic identity and platform of this integration.</p>
                                        </div>
                                        <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">ID</label>
                                                        <input
                                                                :value="shop.id"
                                                                type="text"
                                                                disabled
                                                                class="w-full py-2.5 px-3 border border-gray-200 rounded-lg text-sm outline-none box-border bg-gray-50 text-gray-500 cursor-not-allowed"
                                                        >
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Created</label>
                                                        <input
                                                                :value="formatDate(shop.created)"
                                                                type="text"
                                                                disabled
                                                                class="w-full py-2.5 px-3 border border-gray-200 rounded-lg text-sm outline-none box-border bg-gray-50 text-gray-500 cursor-not-allowed"
                                                        >
                                                </div>
                                                <div class="md:col-span-2">
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Name</label>
                                                        <input
                                                                v-model="form.name"
                                                                type="text"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
                                                        >
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Integration Type</label>
                                                        <select
                                                                v-model="form.integrationType"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border bg-white"
                                                        >
                                                                <option v-for="option in integrationTypeOptions" :key="option" :value="option">{{ option }}</option>
                                                        </select>
                                                        <p class="text-xs text-gray-500 mt-1 mb-0">The e-commerce platform this shop connects with.</p>
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">App</label>
                                                        <select
                                                                v-model="form.app"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border bg-white"
                                                        >
                                                                <option v-for="option in appOptions" :key="option" :value="option">{{ option }}</option>
                                                        </select>
                                                        <p class="text-xs text-gray-500 mt-1 mb-0">Which product experience the shop uses.</p>
                                                </div>
                                        </div>
                                </section>

                                <!-- Connection Details -->
                                <section class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200">
                                                <h3 class="text-base font-semibold text-gray-900 m-0">Connection Details</h3>
                                                <p class="text-sm text-gray-500 mt-1 mb-0">How this shop and the integrator connect: API credentials, cart handover, and production file delivery.</p>
                                        </div>
                                        <div v-if="readOnlyFields.length" class="border-b border-gray-200 py-2 px-6">
                                                <div
                                                        v-for="field in readOnlyFields"
                                                        :key="field.key"
                                                        class="flex flex-wrap gap-2 py-3 border-b border-gray-100 last:border-b-0"
                                                >
                                                        <div class="w-48 text-sm font-medium text-gray-500 shrink-0">{{ field.label }}</div>
                                                        <div class="flex-1 min-w-[12rem] text-sm text-gray-900 break-all">
                                                                <template v-if="field.sensitive && field.value !== '-'">
                                                                        <span class="inline-flex items-center gap-1.5">
                                                                                <button
                                                                                        type="button"
                                                                                        class="font-mono text-left bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded-md py-1 px-2 cursor-pointer text-gray-900"
                                                                                        :title="revealedKeys.has(field.key) ? 'Click to hide' : 'Click to reveal'"
                                                                                        @click="toggleReveal(field.key)"
                                                                                >
                                                                                        {{ revealedKeys.has(field.key) ? field.value : maskValue(field.value) }}
                                                                                </button>
                                                                                <button
                                                                                        type="button"
                                                                                        class="inline-flex items-center justify-center p-1.5 bg-transparent border border-gray-200 rounded-md cursor-pointer text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                                                                                        :title="copiedKey === field.key ? 'Copied!' : 'Copy to clipboard'"
                                                                                        @click="copyToClipboard(field.key, field.value)"
                                                                                >
                                                                                        <svg v-if="copiedKey === field.key" class="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                                                                                        </svg>
                                                                                        <svg v-else class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                                                        </svg>
                                                                                </button>
                                                                                <button
                                                                                        v-if="field.key === 'apiKey'"
                                                                                        type="button"
                                                                                        class="inline-flex items-center justify-center p-1.5 bg-transparent border border-gray-200 rounded-md cursor-pointer text-gray-400 hover:text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                        title="Rotate API key"
                                                                                        :disabled="isRotatingKey"
                                                                                        @click="rotateApiKey"
                                                                                >
                                                                                        <svg class="w-4 h-4" :class="{ 'animate-spin': isRotatingKey }" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                                                                        </svg>
                                                                                </button>
                                                                                <span v-if="copiedKey === field.key" class="text-xs text-green-600">Copied</span>
                                                                        </span>
                                                                </template>
                                                                <span v-else :class="{ 'text-gray-400 italic': field.value === '-' }">{{ field.value }}</span>
                                                        </div>
                                                </div>
                                        </div>
                                        <div class="p-6">
                                                <label class="block text-sm font-medium text-gray-700 mb-2">Cart URL</label>
                                                <input
                                                        v-model="form.cartUrl"
                                                        type="url"
                                                        placeholder="Provided per session by the integrator"
                                                        class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
                                                >
                                                <p v-if="!form.cartUrl" class="text-xs text-gray-500 mt-1 mb-0 flex items-center gap-1">
                                                        <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        No fixed Cart URL is set — the integrator sends the cart URL with each user session. Only fill this in to use one fixed URL for all sessions.
                                                </p>
                                                <p v-else class="text-xs text-gray-500 mt-1 mb-0">This fixed URL is used for all sessions. Leave empty if the integrator sends the cart URL per user session.</p>
                                        </div>
                                        <div class="px-6 pb-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">File Delivery URL</label>
                                                        <input
                                                                v-model="form.fileDeliveryUrl"
                                                                type="url"
                                                                placeholder="Provided per sale by the integrator"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
                                                        >
                                                        <p v-if="!form.fileDeliveryUrl" class="text-xs text-gray-500 mt-1 mb-0 flex items-center gap-1">
                                                                <svg class="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                                </svg>
                                                                No fixed URL is set — the integrator sends it as the callbackUrl parameter in each Sale Notification call. Only fill this in to use one fixed URL for all sales.
                                                        </p>
                                                        <p v-else class="text-xs text-gray-500 mt-1 mb-0">This fixed URL is used for all sales. Leave empty if the integrator sends it as the callbackUrl parameter in the Sale Notification call.</p>
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">File Delivery API Key</label>
                                                        <SecretInput v-model="form.fileDeliveryApiKey" />
                                                        <p class="text-xs text-gray-500 mt-1 mb-0">Only required if the integrator has added authentication to the file delivery URL. Sent along with delivered files to authenticate the request.</p>
                                                </div>
                                        </div>
                                </section>

                                <!-- Pricing & Licensing -->
                                <section class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200">
                                                <h3 class="text-base font-semibold text-gray-900 m-0">Pricing &amp; Licensing</h3>
                                                <p class="text-sm text-gray-500 mt-1 mb-0">Commercial settings for this shop.</p>
                                        </div>
                                        <div class="p-6 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">Price Multiplier</label>
                                                        <input
                                                                v-model="form.priceMultiplier"
                                                                type="number"
                                                                step="0.0001"
                                                                min="0"
                                                                class="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm outline-none box-border"
                                                        >
                                                        <p class="text-xs text-gray-500 mt-1 mb-0">All product prices are multiplied by this factor (1.0 = no change).</p>
                                                </div>
                                                <div>
                                                        <label class="block text-sm font-medium text-gray-700 mb-2">PMS License Key</label>
                                                        <SecretInput v-model="form.pmsLicenseKey" />
                                                        <p class="text-xs text-gray-500 mt-1 mb-0">License key for the print management system, if used.</p>
                                                </div>
                                        </div>
                                </section>

                                <!-- Styling -->
                                <section class="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div class="py-4 px-6 border-b border-gray-200">
                                                <h3 class="text-base font-semibold text-gray-900 m-0">Styling</h3>
                                                <p class="text-sm text-gray-500 mt-1 mb-0">Custom styling for this shop as a JSON document. Leave empty to use the default styling.</p>
                                        </div>
                                        <div class="p-6">
                                                <textarea
                                                        v-model="stylingText"
                                                        rows="12"
                                                        spellcheck="false"
                                                        placeholder="{}"
                                                        class="w-full py-3 px-4 bg-gray-50 border rounded-lg text-xs text-gray-800 font-mono resize-y outline-none box-border"
                                                        :class="stylingError ? 'border-red-400 focus:border-red-500' : 'border-gray-200 focus:border-orange-4'"
                                                        @input="validateStyling"
                                                ></textarea>
                                                <p v-if="stylingError" class="text-xs text-red-600 mt-1.5 mb-0 flex items-start gap-1">
                                                        <svg class="w-3.5 h-3.5 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                                                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                                                        </svg>
                                                        <span>{{ stylingError }}</span>
                                                </p>
                                                <p v-else class="text-xs text-gray-500 mt-1.5 mb-0">Valid JSON ✓ — if you make an edit that breaks the JSON, an error will appear here and saving will be blocked until it's fixed.</p>
                                        </div>
                                </section>

                        </div>
                </main>
        </div>
</template>

<script lang="ts">
import { defineComponent, ref, reactive, computed, onMounted, type Ref, type ComputedRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Swal from 'sweetalert2';
import ShopIntegrationService, { type ShopIntegrationDetailModel } from 'services/ShopIntegrationService';
import SecretInput from '../components/SecretInput.vue';

interface DisplayField {
        key: string;
        label: string;
        value: string;
        sensitive: boolean;
}

// Any credential-like field is masked by default, so new secret-valued
// fields the API may return later are safe without a code change.
const SENSITIVE_KEY_PATTERN = /key|token|secret|password/i;

const EDITABLE_KEYS = [
        'name', 'integrationType', 'cartUrl', 'fileDeliveryUrl',
        'fileDeliveryApiKey', 'priceMultiplier', 'pmsLicenseKey', 'app',
] as const;

type EditableKey = typeof EDITABLE_KEYS[number];
type FormState = Record<EditableKey, string>;

// Friendly labels for known read-only fields; unknown keys fall back to
// a de-camelCased version of the raw key.
const FIELD_LABELS: Record<string, string> = {
        id: 'ID',
        created: 'Created',
        styling: 'Styling',
        apiKey: 'API Key',
};

export default defineComponent({
        name: 'EditShopPage',
        components: { SecretInput },
        setup() {
                const route = useRoute();
                const router = useRouter();
                const routeId = String(route.params.id);

                const shop: Ref<ShopIntegrationDetailModel | null> = ref(null);
                const isLoading: Ref<boolean> = ref(true);
                const isSaving: Ref<boolean> = ref(false);
                const loadError: Ref<string> = ref('');
                const revealedKeys: Ref<Set<string>> = ref(new Set());
                const copiedKey: Ref<string> = ref('');
                let copiedTimer: ReturnType<typeof setTimeout> | undefined;

                const isRotatingKey: Ref<boolean> = ref(false);

                const rotateApiKey = async (): Promise<void> => {
                        const result = await Swal.fire({
                                title: 'Rotate API key?',
                                text: 'A new API key will be generated and the current key will stop working immediately. Any integration still using the old key will break until it is updated.',
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonText: 'Rotate key',
                                cancelButtonText: 'Cancel',
                                confirmButtonColor: '#FF6600',
                        });
                        if (!result.isConfirmed) return;
                        isRotatingKey.value = true;
                        try {
                                const updated = await ShopIntegrationService.rotateApiKey(routeId);
                                if (updated && typeof updated === 'object' && 'apiKey' in updated && shop.value) {
                                        shop.value = { ...shop.value, ...updated };
                                } else {
                                        await fetchShop();
                                }
                                revealedKeys.value = new Set();
                                Swal.fire({
                                        title: 'API key rotated',
                                        text: 'A new API key has been generated. Update any integration that still uses the old key.',
                                        icon: 'success',
                                        confirmButtonColor: '#FF6600',
                                });
                        } catch {
                                Swal.fire({
                                        title: 'Error',
                                        text: 'Could not rotate the API key',
                                        icon: 'error',
                                        confirmButtonColor: '#FF6600',
                                });
                        } finally {
                                isRotatingKey.value = false;
                        }
                };

                const copyToClipboard = async (key: string, value: string): Promise<void> => {
                        try {
                                await navigator.clipboard.writeText(value);
                                copiedKey.value = key;
                                if (copiedTimer) clearTimeout(copiedTimer);
                                copiedTimer = setTimeout(() => { copiedKey.value = ''; }, 2000);
                        } catch {
                                Swal.fire({
                                        title: 'Error',
                                        text: 'Could not copy to clipboard',
                                        icon: 'error',
                                        confirmButtonColor: '#FF6600',
                                });
                        }
                };

                const stylingText: Ref<string> = ref('');
                const stylingError: Ref<string> = ref('');

                const form: FormState = reactive({
                        name: '',
                        integrationType: '',
                        cartUrl: '',
                        fileDeliveryUrl: '',
                        fileDeliveryApiKey: '',
                        priceMultiplier: '',
                        pmsLicenseKey: '',
                        app: '',
                });

                const INTEGRATION_TYPES = ['custom', 'promidata', 'woocommerce', 'shopify', 'magento'];
                const APPS = ['editor', 'kiosk'];

                // If the API returns a value outside the supported list, keep it
                // visible (and selectable) instead of showing a blank control.
                const withCurrentValue = (options: string[], current: string): string[] => (
                        current && !options.includes(current) ? [current, ...options] : options
                );

                const integrationTypeOptions: ComputedRef<string[]> = computed(() => withCurrentValue(INTEGRATION_TYPES, form.integrationType));
                const appOptions: ComputedRef<string[]> = computed(() => withCurrentValue(APPS, form.app));

                const toFormValue = (value: unknown): string => (value === null || value === undefined ? '' : String(value));

                // Normalise a styling value (string or object) to a pretty-printed JSON
                // string suitable for the textarea, or '' when absent.
                const normaliseStyling = (raw: unknown): string => {
                        if (raw === null || raw === undefined || raw === '') return '';
                        if (typeof raw === 'string') {
                                try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
                        }
                        return JSON.stringify(raw, null, 2);
                };

                const populateForm = (data: ShopIntegrationDetailModel): void => {
                        EDITABLE_KEYS.forEach((key: EditableKey) => {
                                form[key] = toFormValue(data[key]);
                        });
                        stylingText.value = normaliseStyling(data.styling);
                        stylingError.value = '';
                };

                const validateStyling = (): void => {
                        const text = stylingText.value.trim();
                        if (text === '') { stylingError.value = ''; return; }
                        try {
                                JSON.parse(text);
                                stylingError.value = '';
                        } catch (e) {
                                stylingError.value = e instanceof Error ? e.message : 'Invalid JSON';
                        }
                };

                const hasStylingChanges: ComputedRef<boolean> = computed(() => {
                        const original = normaliseStyling(shop.value?.styling);
                        const current = stylingText.value.trim();
                        const originalTrimmed = original.trim();
                        if (current === '' && originalTrimmed === '') return false;
                        // Compare normalised parsed forms so cosmetic reformatting doesn't count.
                        try {
                                if (current === '' || originalTrimmed === '') return current !== originalTrimmed;
                                return JSON.stringify(JSON.parse(current)) !== JSON.stringify(JSON.parse(originalTrimmed));
                        } catch {
                                return current !== originalTrimmed;
                        }
                });

                const hasChanges: ComputedRef<boolean> = computed(() => {
                        if (!shop.value) return false;
                        return EDITABLE_KEYS.some((key: EditableKey) => form[key] !== toFormValue(shop.value?.[key]))
                                || hasStylingChanges.value;
                });

                const formatValue = (value: unknown): string => {
                        if (value === null || value === undefined || value === '') return '-';
                        if (typeof value === 'object') return JSON.stringify(value);
                        return String(value);
                };

                const labelFor = (key: string): string => FIELD_LABELS[key]
                        ?? key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

                // Render every non-editable field the API returned, not a hardcoded subset.
                const readOnlyFields: ComputedRef<DisplayField[]> = computed(() => {
                        if (!shop.value) return [];
                        return Object.entries(shop.value)
                                .filter(([key]) => !(EDITABLE_KEYS as readonly string[]).includes(key)
                                        && !['id', 'created', 'styling'].includes(key))
                                .map(([key, value]) => ({
                                        key,
                                        label: labelFor(key),
                                        value: formatValue(value),
                                        sensitive: SENSITIVE_KEY_PATTERN.test(key),
                                }));
                });

                // Styling is a JSON document; pretty-print it whether the API
                // returns it as a string or an already-parsed object.
                const stylingJson: ComputedRef<string> = computed(() => {
                        const raw = shop.value?.styling;
                        if (raw === null || raw === undefined || raw === '') return '';
                        if (typeof raw === 'string') {
                                try {
                                        return JSON.stringify(JSON.parse(raw), null, 2);
                                } catch {
                                        return raw;
                                }
                        }
                        return JSON.stringify(raw, null, 2);
                });

                const maskValue = (value: string): string => '•'.repeat(Math.min(Math.max(value.length, 8), 24));

                const toggleReveal = (key: string): void => {
                        const next = new Set(revealedKeys.value);
                        if (next.has(key)) {
                                next.delete(key);
                        } else {
                                next.add(key);
                        }
                        revealedKeys.value = next;
                };

                const formatDate = (value: string): string => {
                        const date = new Date(value);
                        if (Number.isNaN(date.getTime())) return value;
                        return date.toLocaleString();
                };

                const goBack = (): void => {
                        router.back();
                };

                const fetchShop = async (): Promise<void> => {
                        isLoading.value = true;
                        loadError.value = '';
                        try {
                                const data = await ShopIntegrationService.getIntegration(routeId);
                                shop.value = data;
                                populateForm(data);
                        } catch (error: unknown) {
                                const errorMessage = error instanceof Error ? error.message : 'Failed to load shop integration';
                                loadError.value = errorMessage;
                                Swal.fire({
                                        title: 'Error',
                                        text: errorMessage,
                                        icon: 'error',
                                        confirmButtonColor: '#FF6600',
                                });
                        } finally {
                                isLoading.value = false;
                        }
                };

                const save = async (): Promise<void> => {
                        if (!shop.value || !hasChanges.value || isSaving.value) return;
                        if (stylingError.value) return; // blocked by invalid JSON
                        isSaving.value = true;
                        try {
                                // Only send the fields that actually changed.
                                const changes: Partial<Record<EditableKey, string | null> & { styling: string | null }> = {};
                                EDITABLE_KEYS.forEach((key: EditableKey) => {
                                        if (form[key] !== toFormValue(shop.value?.[key])) {
                                                changes[key] = form[key] === '' ? null : form[key];
                                        }
                                });
                                if (hasStylingChanges.value) {
                                        const trimmed = stylingText.value.trim();
                                        // Send the validated JSON string — the API stores styling as a
                                        // string (ShopIntegrationDetailModel.styling: string | null).
                                        changes.styling = trimmed === '' ? null : trimmed;
                                }
                                const updated = await ShopIntegrationService.updateIntegration(routeId, changes);
                                shop.value = updated;
                                populateForm(updated);
                                Swal.fire({
                                        title: 'Saved',
                                        text: 'Shop integration updated successfully',
                                        icon: 'success',
                                        timer: 1600,
                                        showConfirmButton: false,
                                });
                        } catch (error: unknown) {
                                const errorMessage = error instanceof Error ? error.message : 'Failed to update shop integration';
                                Swal.fire({
                                        title: 'Error',
                                        text: errorMessage,
                                        icon: 'error',
                                        confirmButtonColor: '#FF6600',
                                });
                        } finally {
                                isSaving.value = false;
                        }
                };

                onMounted(fetchShop);

                return {
                        routeId,
                        shop,
                        isLoading,
                        isSaving,
                        loadError,
                        form,
                        integrationTypeOptions,
                        appOptions,
                        hasChanges,
                        readOnlyFields,
                        stylingJson,
                        stylingText,
                        stylingError,
                        validateStyling,
                        revealedKeys,
                        copiedKey,
                        copyToClipboard,
                        isRotatingKey,
                        rotateApiKey,
                        maskValue,
                        toggleReveal,
                        formatDate,
                        fetchShop,
                        save,
                        goBack,
                };
        },
});
</script>
